import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogIn, Mail, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { CarbonMark, VortexWordmark } from "../components/VortexWordmark";

const formatErr = (detail) => {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
};

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
    </svg>
);

/**
 * LoginPage — Carbon Labs standalone researcher-access gateway.
 *
 * This page is rendered OUTSIDE the Header/Footer/Newsletter/Chat chrome via
 * App.js so unauthenticated visitors never see any part of the storefront.
 * Full-viewport dark layout, carbon-fibre texture, centred authentication
 * panel, compliance strip under the form.
 */
export const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fieldError, setFieldError] = useState(null);
    const searchParams = new URLSearchParams(location.search);
    const nextParam = searchParams.get("next");
    const redirect = nextParam || location.state?.from || "/";

    const submit = async (e) => {
        e.preventDefault();
        setFieldError(null);
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate(redirect, { replace: true });
        } catch (err) {
            const msg = formatErr(err.response?.data?.detail) || err.message;
            setFieldError(msg);
            toast.error(msg);
        }
        setSubmitting(false);
    };

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const callback = window.location.origin + "/auth/callback";
        const redirectUrl = nextParam
            ? `${callback}?next=${encodeURIComponent(nextParam)}`
            : callback;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div
            className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-10 relative overflow-hidden"
            data-testid="login-page"
            style={{
                background:
                    "radial-gradient(circle at 50% 0%, rgba(42,187,201,0.08) 0%, rgba(10,12,14,1) 55%), #060708",
            }}
        >
            {/* Carbon-fibre weave overlay */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.22]"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(45deg, rgba(255,255,255,0.045) 0 1px, transparent 1px 6px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.045) 0 1px, transparent 1px 6px)",
                }}
            />
            {/* Subtle brushed gunmetal top band */}
            <div
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-40 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(60,70,78,0.28) 0%, rgba(60,70,78,0) 100%)",
                }}
            />
            {/* Cyan glow spotlight */}
            <div
                aria-hidden="true"
                className="absolute left-1/2 -translate-x-1/2 top-1/3 w-[520px] h-[520px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(42,187,201,0.14) 0%, rgba(42,187,201,0) 70%)",
                    filter: "blur(40px)",
                }}
            />

            <div className="relative z-10 w-full max-w-[420px] flex flex-col items-center">
                {/* Logo mark + wordmark */}
                <Link
                    to="/login"
                    className="flex flex-col items-center gap-3 mb-6"
                    aria-label="Carbon Labs"
                    data-testid="gateway-logo"
                >
                    <CarbonMark size={56} />
                    <VortexWordmark size="md" variant="static" showIcon={false} showPillars={false} />
                </Link>

                {/* Heading block */}
                <h1
                    className="font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight text-center"
                    data-testid="gateway-heading"
                >
                    Secure Researcher Access
                </h1>
                <p className="mt-2 text-sm text-white/60 text-center max-w-[340px]">
                    Sign in to access Carbon Labs.
                </p>

                {/* Authentication panel */}
                <div
                    className="w-full mt-7 rounded-2xl p-6 sm:p-7"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(22,25,28,0.9) 0%, rgba(14,16,18,0.9) 100%)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        boxShadow:
                            "0 1px 0 rgba(255,255,255,0.04) inset, 0 20px 60px rgba(0,0,0,0.6)",
                    }}
                    data-testid="gateway-panel"
                >
                    <button
                        onClick={handleGoogle}
                        data-testid="google-signin-btn"
                        type="button"
                        className="w-full flex items-center justify-center gap-3 px-5 py-2.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-white/90 transition-colors"
                    >
                        <GoogleIcon /> Continue with Google
                    </button>

                    <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-white/8" />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                            or with email
                        </span>
                        <div className="flex-1 h-px bg-white/8" />
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">
                                Email
                            </label>
                            <div className="relative">
                                <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 bg-black/50 border border-white/10 rounded-full text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#2abbc9] focus:ring-1 focus:ring-[#2abbc9]/40 transition-all"
                                    placeholder="you@lab.com"
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">
                                Password
                            </label>
                            <div className="relative">
                                <KeyRound size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    type={showPwd ? "text" : "password"}
                                    required
                                    minLength={8}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-11 py-2.5 bg-black/50 border border-white/10 rounded-full text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#2abbc9] focus:ring-1 focus:ring-[#2abbc9]/40 transition-all"
                                    placeholder="At least 8 characters"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((s) => !s)}
                                    aria-label={showPwd ? "Hide password" : "Show password"}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white transition-colors"
                                    data-testid="login-password-toggle"
                                >
                                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {fieldError && (
                            <p
                                role="alert"
                                className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2"
                                data-testid="login-error"
                            >
                                {fieldError}
                            </p>
                        )}

                        <div className="flex justify-end -mt-1">
                            <Link
                                to="/forgot-password"
                                className="text-[11px] text-[#2abbc9] hover:text-white font-mono uppercase tracking-widest transition-colors"
                                data-testid="forgot-password-link"
                            >
                                Forgot password?
                            </Link>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                background:
                                    "linear-gradient(90deg, #2abbc9 0%, #4fd1d9 100%)",
                                boxShadow:
                                    "0 6px 24px rgba(42,187,201,0.35), 0 1px 0 rgba(255,255,255,0.25) inset",
                            }}
                            data-testid="login-submit-btn"
                        >
                            <LogIn size={14} />
                            {submitting ? "Signing in…" : "Sign In"}
                        </button>
                    </form>

                    <p className="text-center mt-6 text-xs text-white/50">
                        New to Carbon Labs?{" "}
                        <Link
                            to={nextParam ? `/register?next=${encodeURIComponent(nextParam)}` : "/register"}
                            className="text-[#4fd1d9] hover:text-white font-semibold transition-colors"
                            data-testid="register-link"
                        >
                            Create Account
                        </Link>
                    </p>
                </div>

                {/* Compliance strip */}
                <div
                    className="w-full mt-5 rounded-full px-4 py-2 flex items-center justify-center gap-2"
                    style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.07)",
                    }}
                    data-testid="compliance-strip"
                >
                    <ShieldCheck size={12} className="text-[#2abbc9] shrink-0" aria-hidden="true" />
                    <p className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] text-white/70 text-center">
                        Not for human use, for research purposes only
                    </p>
                </div>

                <p className="mt-6 text-[10px] font-mono uppercase tracking-widest text-white/30">
                    Carbon Labs · Secure Portal
                </p>
            </div>
        </div>
    );
};
